import React, { useEffect } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { CalendarPlus, Download, RotateCcw, PartyPopper } from "lucide-react";
import { buildICS, downloadFile, activityLabel } from "@/lib/api";
import ConnectionBurst from "@/components/ConnectionBurst";

export default function ConfirmationScreen({ registration, settings, onReplayVideo }) {
  const attending = registration?.attending === "yes";

  useEffect(() => {
    if (!attending) return;
    const colors = ["#f5c542", "#e60000", "#e60000", "#e60000"];
    const end = Date.now() + 1200;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 120, spread: 90, origin: { y: 0.4 }, colors });
  }, [attending]);

  const addCalendar = () => {
    downloadFile("convention-rea-company.ics", buildICS(settings, registration), "text/calendar");
  };
  const downloadReminder = () => {
    const txt = `PROMEMORIA — Convention Rea Company Srl x Fastweb + Vodafone

Nome: ${registration.first_name} ${registration.last_name}
Data: ${settings.event_date}
Orario: ${settings.event_time}
Location: ${settings.location}, ${settings.city}
Attività: ${activityLabel[registration.tournament] || "Convention"}

Preparati a vivere connessione, energia e grandi sfide!`;
    downloadFile("promemoria-convention.txt", txt);
  };

  return (
    <section className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-6 py-24" data-testid="confirmation-screen">
      <div className="glow-underlay" />
      {attending && <ConnectionBurst className="z-0" />}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0f0f13]/80 p-8 text-center backdrop-blur-xl sm:p-12"
      >
        <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[#e60000] to-[#e60000] shadow-[0_0_30px_rgba(230, 0, 0,0.5)]">
          <PartyPopper size={30} className="text-white" />
        </div>

        {attending ? (
          <>
            <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl text-glow-magenta">
              SEI UFFICIALMENTE DEI NOSTRI!
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-white/70">
              La tua partecipazione è stata registrata. Preparati a vivere una giornata di connessioni,
              energia e grandi sfide.
            </p>

            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Torneo</div>
                <div className="mt-1 font-display font-bold">{activityLabel[registration.tournament]}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Data & Luogo</div>
                <div className="mt-1 font-display font-bold">{settings.event_date}</div>
                <div className="text-sm text-white/60">{settings.location}, {settings.city}</div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button onClick={addCalendar} className="btn-neon flex items-center gap-2 rounded-full px-6 py-3 text-xs font-extrabold uppercase tracking-wider" data-testid="btn-add-calendar">
                <CalendarPlus size={16} /> Aggiungi al calendario
              </button>
              <button onClick={downloadReminder} className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-extrabold uppercase tracking-wider text-white transition-colors hover:bg-white/5" data-testid="btn-download-reminder">
                <Download size={16} /> Scarica promemoria
              </button>
              <button onClick={onReplayVideo} className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-extrabold uppercase tracking-wider text-white transition-colors hover:bg-white/5" data-testid="btn-replay-video">
                <RotateCcw size={16} /> Rivedi il video
              </button>
            </div>

            {registration?.edit_token && (
              <p className="mt-6 text-xs text-white/40">
                Vuoi modificare la tua risposta? Salva questo link:{" "}
                <a className="text-[#f5c542] underline" href={`/edit/${registration.edit_token}`} data-testid="edit-link">
                  Modifica risposta
                </a>
              </p>
            )}
          </>
        ) : (
          <>
            <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
              GRAZIE PER AVERCI INFORMATO
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-white/70">
              Abbiamo registrato la tua risposta. Ci dispiace che tu non possa essere con noi.
            </p>
          </>
        )}
      </motion.div>
    </section>
  );
}
